import {
  DowngradeSubscriptionDto,
  SetBackupSubscriptionDto,
  UpgradeSubscriptionDto,
  ResponseErrorDto,
} from '@interfaces/models';
import {
  getAvailableSubscriptions,
  sendDowngradeFormula,
  sendUpgradeFormula,
  setBackupFormula,
} from '@api/subscription';
import { useSubscriptionInitialData, useCurrentUser } from '@shared/hooks';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '@ui/components';

interface UseSubscriptionsProps {
  downgradeSubscriptionSuccess?: () => void;
  setBackupSubscriptionSuccess?: () => void;
  upgradeSubscriptionSuccess?: () => void;
}

export const useSubscriptions = ({
  downgradeSubscriptionSuccess,
  setBackupSubscriptionSuccess,
  upgradeSubscriptionSuccess,
}: Partial<UseSubscriptionsProps> = {}) => {
  const { user } = useCurrentUser();
  const { t } = useTranslation();

  const { data: initialData, isLoading: isInitialDataLoading } = useSubscriptionInitialData({
    companyType: user?.company?.companyType,
  });

  const {
    data: subscriptions,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['subscriptions'],
    queryFn: () => getAvailableSubscriptions(),
  });

  const { mutate: upgradeSubscription, isPending: isUpgradeSubscriptionPending } = useMutation({
    mutationFn: (request: UpgradeSubscriptionDto) => sendUpgradeFormula(request),
    onSuccess: upgradeSubscriptionSuccess,
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const { mutate: downgradeSubscription, isPending: isDowngradeSubscriptionPending } = useMutation({
    mutationFn: (request: DowngradeSubscriptionDto) => sendDowngradeFormula(request),
    onSuccess: downgradeSubscriptionSuccess,
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const { mutate: setBackupSubscription, isPending: isSetBackupSubscriptionPending } = useMutation({
    mutationFn: (request: SetBackupSubscriptionDto) => setBackupFormula(request),
    onSuccess: setBackupSubscriptionSuccess,
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  return {
    isDowngradeSubscriptionPending,
    isSetBackupSubscriptionPending,
    isUpgradeSubscriptionPending,
    setBackupSubscription,
    downgradeSubscription,
    isInitialDataLoading,
    upgradeSubscription,
    subscriptions,
    initialData,
    isLoading,
    refetch,
  };
};
